import { useMutation } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import {
  createDocumentVersionSession,
  updateDocumentVersionSession,
  createDocumentVersionSessionEvent,
  updateDocumentVersionSessionEvent,
} from '@/lib/api/client'
import { useToast } from '@/components/ui/use-toast'

import { useTechnicianPageStore } from '@/pages/TechnicianPage/hooks/useTechnicianPageStore'

const safeIncr = (value: number | undefined, incrBy: number) => {
  if (typeof value === 'number') {
    return value + incrBy
  }
}

export const useCreateDocumentVersionSession = () => {
  const { toast } = useToast()

  const setCurrentSessionId = useTechnicianPageStore(
    useShallow((state) => state.setCurrentSessionId),
  )

  return useMutation({
    mutationFn: async ({
      documentVersionId,
      username,
    }: {
      documentVersionId: string
      username: string
    }) => {
      return createDocumentVersionSession(documentVersionId, {
        username,
      })
    },
    onSuccess: (data) => {
      setCurrentSessionId(data.id || null)
    },
    onError: () => {
      setCurrentSessionId(null)
      toast({
        title: 'Error',
        description: 'Failed to create session',
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateDocumentVersionSession = () => {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      documentVersionSessionId,
      completedTimestamp,
      username,
    }: {
      documentVersionSessionId: string
      completedTimestamp: string
      username: string
    }) => {
      return updateDocumentVersionSession(documentVersionSessionId, {
        json_data: {
          username,
        },
        completed: completedTimestamp,
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update session',
        variant: 'destructive',
      })
    },
  })
}

export const useCreateDocumentVersionSessionEvent = () => {
  const { toast } = useToast()
  const setLastSessionEventId = useTechnicianPageStore(
    useShallow((state) => state.setLastSessionEventId),
  )

  return useMutation({
    mutationFn: async ({
      documentVersionSessionId,
      documentPageId,
      buttonLabel,
      completedTimestamp,
      subPageIndex,
    }: {
      documentVersionSessionId: string
      documentPageId: string
      buttonLabel: string
      completedTimestamp?: string
      subPageIndex?: number
    }) => {
      const sessionEvent = await createDocumentVersionSessionEvent(
        documentVersionSessionId,
        {
          document_page_id: documentPageId,
          button_label: buttonLabel,
          json_data: { page: safeIncr(subPageIndex, 1) },
        },
      )

      if (sessionEvent.id && completedTimestamp) {
        await updateDocumentVersionSessionEvent(sessionEvent.id, {
          completed: completedTimestamp,
          json_data: { page: safeIncr(subPageIndex, 1) },
        })
      }

      return sessionEvent
    },
    onSuccess: (sessionEvent) => {
      // Keep the store aligned with the most recently created event before
      // any caller runs follow-up work such as ending the session.
      setLastSessionEventId(sessionEvent.id || null)
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to create session event',
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateDocumentVersionSessionEvent = () => {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      documentVersionSessionEventId,
      completedTimestamp,
      subPageIndex,
    }: {
      documentVersionSessionEventId: string
      completedTimestamp: string
      subPageIndex?: number
    }) => {
      return updateDocumentVersionSessionEvent(documentVersionSessionEventId, {
        completed: completedTimestamp,
        json_data: { page: safeIncr(subPageIndex, 1) },
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update session event',
        variant: 'destructive',
      })
    },
  })
}
